import {groupBy, mapObject} from '@benchling/js-utils';

import {getApolloClient} from 'graphql-client/ApolloClient';
import {
  updateProcedureMethodExecutionInstances,
  UpdateProcedureMethodExecutionInstancesResponse,
} from 'procedures/network/ProcedureMethodExecutionInstanceDAO';
import {UnitOperationFields} from 'studies/typed/process-development/unit-op-linking-flowchart/useUnitOpLinkingState';

import {PROCEDURE_RUN_DETAILS_VIEW_QUERY} from './ProcedureRunDetailsView';
import {UnitOpLinkEdge} from './unit-op-linking-flowchart/UnitOpLinkingFlowchartUtils';

export interface ProcedureMethodExecutionInstanceAssociationUpdateByTargetId {
  targetProcedureMethodExecutionInstanceId: string;
  sourceProcedureMethodExecutionInstanceIds: Array<string>;
}

/**
 * Transforms editor edges into procedure method execution instance association updates.
 * Groups edges by destination, maps to source IDs, and creates update objects
 * for each known destination PMEI with its associated source PMEIs.
 *
 * @param edgesFromEditor - Editor edges with source/destination PMEI info
 * @param knownDestinationPMEIs - Valid destination PMEIs
 * @returns Array of updates with target IDs and their source IDs
 */
export function mapEdgesToUpdates(
  edgesFromEditor: Array<UnitOpLinkEdge>,
  knownDestinationPMEIs: Array<UnitOperationFields>
): Array<ProcedureMethodExecutionInstanceAssociationUpdateByTargetId> {
  const edgesByDestinationId = groupBy(edgesFromEditor, (edge) => edge.destinationPMEIId);
  const sourcesByDestinationId = mapObject(edgesByDestinationId, (edges) =>
    edges.map((edge) => edge.sourcePMEIId)
  );
  const sourceIdsByTargetId: {[targetId: string]: Array<string>} = {};
  knownDestinationPMEIs.forEach((destinationPMEI) => {
    sourceIdsByTargetId[destinationPMEI.pmeiId] = sourcesByDestinationId[destinationPMEI.pmeiId] ?? [];
  });
  return Object.entries(sourceIdsByTargetId).map(([targetId, sourceIds]) => ({
    targetProcedureMethodExecutionInstanceId: targetId,
    sourceProcedureMethodExecutionInstanceIds: sourceIds,
  }));
}

export async function bulkSetProcedureMethodExecutionInstanceAssociations(
  updatesByTargetId: Array<ProcedureMethodExecutionInstanceAssociationUpdateByTargetId>
): Promise<UpdateProcedureMethodExecutionInstancesResponse> {
  const response = await updateProcedureMethodExecutionInstances(
    updatesByTargetId.map((updatesByTargetId) => ({
      id: updatesByTargetId.targetProcedureMethodExecutionInstanceId,
      sourceExperimentMethodInstanceIds: updatesByTargetId.sourceProcedureMethodExecutionInstanceIds,
    }))
  );
  getApolloClient().refetchQueries({include: [PROCEDURE_RUN_DETAILS_VIEW_QUERY]});
  return response;
}
